import React, { ReactElement } from 'react';

import { SlideOut } from '@components/common';
import { Portal } from '@components/Portal';
import { NewStudyButton } from '@components/StudiesApp/components/StudyNew/components/NewStudyButton';
import { SLIDEOUT_BUTTON_KINDS } from '@components/StudiesApp/components/StudyNew/constants';
import { NewStudySpriteSVG } from '@components/svgs/NewStudySpriteSVG';
import { useFeature } from '@hooks/useFeature';
import { useUser } from '@hooks/useUser';
import { UserTeamsChannel, useWebsocket } from '@hooks/useWebsocket';
import { useGetWhoamiQuery } from '@stores/whoami';

import { NoTeamAlert } from './NoTeamAlert';

interface ButtonsProps {
  disabled?: boolean;
  teamId?: number | null;
  onClose?: () => void;
  externalCandidatesDefaulted?: boolean;
}

type Props = ButtonsProps & { isVisible: boolean };

export const NewRecruitingStudySlideout = ({ teamId, isVisible, onClose }: Props): ReactElement | null => {
  return <NewStudySlideout teamId={teamId} isVisible={isVisible} onClose={onClose} externalCandidatesDefaulted />;
};

export const NewStudySlideout = ({
  teamId,
  isVisible,
  onClose,
  externalCandidatesDefaulted = false
}: Props): ReactElement | null => {
  const teamAccessControlEnabled = useFeature('team_access_control');
  const { id: user_id } = useUser();
  const { data: whoami, isLoading, refetch } = useGetWhoamiQuery();

  const teams = whoami?.user?.teams || [];

  useWebsocket<{ user_id: number }, void>({
    channel: UserTeamsChannel,
    params: { user_id },
    onReceived: refetch
  });

  if (!isVisible) {
    return null;
  }

  const showNeedsTeamError = teamAccessControlEnabled && !teams?.length;

  return (
    <section>
      <SlideOut
        title='Create new study'
        subtitle='Choose a research method to run with internal or external candidates.'
        onClose={onClose}
      >
        <div className='flex flex-col space-y-4 border-t border-gray-200 p-6'>
          {showNeedsTeamError && <NoTeamAlert className='mb-2' />}
          {SLIDEOUT_BUTTON_KINDS.map((kind) => (
            <NewStudyButton
              key={kind}
              kind={kind}
              disabled={showNeedsTeamError || isLoading}
              teamId={teamId}
              onClose={onClose}
              externalCandidatesDefaulted={externalCandidatesDefaulted}
            />
          ))}
        </div>
      </SlideOut>
      <Portal>
        <NewStudySpriteSVG style={{ display: 'none' }} />
      </Portal>
    </section>
  );
};
